// ─────────────────────────────────────────────────────────────────────────────
// useOnlineStatus Hook - TypeScript Implementation
// React hook to expose network connectivity status with type safety
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import networkService from "./services/networkService";
import { NetworkState } from "../types";

// Hook configuration interface
interface UseOnlineStatusConfig {
  pollInterval?: number; // milliseconds
}

// Hook return type
interface OnlineStatus {
  online: boolean;
  lastChecked: Date | null;
  checking: boolean;
}

/**
 * React hook to monitor network connectivity status
 *
 * @param config Configuration options for the hook
 * @returns Current network status state
 *
 * @example
 * ```tsx
 * const { online, checking, lastChecked } = useOnlineStatus({ pollInterval: 30000 });
 *
 * if (checking) return <div>Checking connection...</div>;
 * if (!online) return <div>You're offline</div>;
 * ```
 */
export default function useOnlineStatus(
  config: UseOnlineStatusConfig = {}
): OnlineStatus {
  const { pollInterval } = config;

  const [state, setState] = useState<OnlineStatus>(() => {
    // Get initial state from network service
    const initialState = networkService.getState();
    return {
      online: initialState.online,
      lastChecked: initialState.lastChecked,
      checking: initialState.checking,
    };
  });

  useEffect(() => {
    // Subscribe to network state changes
    const unsubscribe = networkService.subscribe(
      (networkState: NetworkState) => {
        setState({
          online: networkState.online,
          lastChecked: networkState.lastChecked,
          checking: networkState.checking,
        });
      }
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [pollInterval]); // Re-subscribe if poll interval changes

  return state;
}

// Export the hook type for use in other components
export type { UseOnlineStatusConfig, OnlineStatus };
